import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  camelCase, get, last, size, toNumber, toString, upperFirst,
} from 'lodash/fp';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  Button, FormControlLabel, Radio, RadioGroup, Typography,
} from '@material-ui/core';
import messages from '../messages';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Instruction: styled(Typography)`
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 700;
  `,
  Row: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    order: -1;
    margin-bottom: 1.5rem;
  `,
  Info: styled(Typography)`
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
  `,
  Number: styled.span`
    margin-right: 4px;
  `,
  Type: styled.span`
    margin: 0 4px;
  `,
  OptionCount: styled.span`
    margin-left: 4px;
  `,
  RadioGroup: styled(RadioGroup)`
    flex-direction: row;
    margin-bottom: 1.5rem;
  `,
  Radio: styled(Radio)`
    color: ${({ theme }) => theme.palette.grey[600]};
    &.Mui-checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  FormControlLabel: styled(({ classes, ...otherProps }) => (
    <FormControlLabel
      classes={{ label: 'label' }}
      {...otherProps}
    />
  ))`
    :not(:last-child) {
      margin-right: 24px;
    }

    .label {
      color: ${({ theme }) => theme.palette.grey[900]};
      font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    }
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
  `,
  Button: styled(Button)`
    min-width: 0;
    min-height: 32px;
    padding: 3px 8px;
    :not(:first-child) {
      margin-left: 8px;
    }
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :disabled {
      color: ${({ theme }) => theme.palette.primary.contrastText};
    }
    ${({ variant, theme }) => variant === 'outlined' && css`
      color: ${theme.palette.grey[600]};
    `}
  `,
};

const SelectRequiredPaper = ({
  finishStep, intl, setFieldValue, questions, setStep, type,
}) => {
  const goPrevStep = useCallback(() => {
    setFieldValue(`questions[${size(questions) - 1}].required`, false);

    setStep(prevStep => prevStep - 1);
  }, [questions, setFieldValue, setStep]);

  const handleChange = useCallback((event) => {
    setFieldValue(`questions[${size(questions) - 1}].required`, !!toNumber(event.target.value));
  }, [questions, setFieldValue]);

  return (
    <S.Wrapper>
      <S.Instruction>
        <FormattedMessage {...messages.selectRequiredPaperInstruction} />
      </S.Instruction>
      <S.Row>
        <S.Info>
          <S.Number>
            <FormattedMessage
              {...messages.selectRequiredPaperNumber}
              values={{ number: size(questions) }}
            />
          </S.Number>
          /
          <S.Type>
            <FormattedMessage
              {...messages[`selectRequiredPaperType${upperFirst(camelCase(type))}`]}
            />
          </S.Type>
          /
          <S.OptionCount>
            <FormattedMessage
              {...messages.selectRequiredPaperOptionCount}
              values={{ count: size(get('options', last(questions))) }}
            />
          </S.OptionCount>
        </S.Info>
      </S.Row>
      <S.RadioGroup
        aria-label={intl.formatMessage(messages.selectRequiredPaperRadioGroupAriaLabel)}
        name="required"
        value={toString(toNumber(get('required', last(questions))))}
        onChange={handleChange}
      >
        <S.FormControlLabel
          value="0"
          control={<S.Radio color="primary" />}
          label={intl.formatMessage(messages.selectRequiredPaperRadioUnrequired)}
        />
        <S.FormControlLabel
          value="1"
          control={<S.Radio color="primary" />}
          label={intl.formatMessage(messages.selectRequiredPaperRadioRequired)}
        />
      </S.RadioGroup>
      <S.Actions>
        <S.Button
          type="button"
          variant="outlined"
          onClick={goPrevStep}
        >
          <FormattedMessage {...messages.selectRequiredPaperButtonPrev} />
        </S.Button>
        <S.Button
          type="button"
          color="primary"
          variant="contained"
          onClick={finishStep}
        >
          <FormattedMessage {...messages.selectRequiredPaperButtonFinish} />
        </S.Button>
      </S.Actions>
    </S.Wrapper>
  );
};

SelectRequiredPaper.propTypes = {
  finishStep: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    number: PropTypes.number.isRequired,
    position: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }).isRequired,
    required: PropTypes.bool.isRequired,
    size: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }).isRequired,
    type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE']).isRequired,
  })),
  setFieldValue: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  type: PropTypes.string,
};

SelectRequiredPaper.defaultProps = {
  questions: null,
  type: null,
};

export default injectIntl(SelectRequiredPaper);
